import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, PhotoUpload, FileLink } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

function fmtDate(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString("de-DE"); } catch { return d; } }

export default function VorfaellePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), incident: "", action: "", responsible: "", photo_file_id: null });

  const load = () => api.get("/incidents").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.incident.trim() || !form.action.trim()) { toast.error("Vorfall und Maßnahme erforderlich"); return; }
    setBusy(true);
    try {
      await api.post("/incidents", form);
      toast.success("Vorfall dokumentiert");
      setForm({ date: new Date().toISOString().slice(0, 10), incident: "", action: "", responsible: "", photo_file_id: null });
      setOpen(false); load();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/incidents/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Vorfälle & Korrekturmaßnahmen" subtitle="Abweichungen dokumentieren – Grenzwertüberschreitungen werden automatisch erfasst">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-incident"><Plus className="h-4 w-4 mr-1" /> Vorfall erfassen</Button></DialogTrigger>
          <DialogContent data-testid="incident-dialog">
            <DialogHeader><DialogTitle>Vorfall & Maßnahme</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Datum</Label>
                  <Input type="date" data-testid="input-incident-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Verantwortlich</Label>
                  <Input data-testid="input-incident-responsible" value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} placeholder="Name" /></div>
              </div>
              <div className="space-y-1.5"><Label>Vorfall / Abweichung *</Label>
                <Textarea data-testid="input-incident-desc" value={form.incident} onChange={(e) => setForm({ ...form, incident: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Durchgeführte Maßnahme *</Label>
                <Textarea data-testid="input-incident-action" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Foto</Label>
                <PhotoUpload fileId={form.photo_file_id} onChange={(id) => setForm({ ...form, photo_file_id: id })} testid="input-incident-photo" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button data-testid="btn-submit-corrective-action" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><AlertTriangle className="h-10 w-10 mx-auto mb-2" />Keine Vorfälle dokumentiert</div>
      ) : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="incident-row">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge variant="neutral">{fmtDate(i.date)}</StatusBadge>
                  {i.auto && <StatusBadge variant="warning">Auto: Grenzwert</StatusBadge>}
                  {i.responsible && <span className="text-xs text-slate-500">{i.responsible}</span>}
                </div>
                <button onClick={() => del(i.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-2">
                <div className="text-sm font-semibold text-slate-800">Vorfall</div>
                <p className="text-sm text-slate-600">{i.incident}</p>
              </div>
              <div className="mt-2">
                <div className="text-sm font-semibold text-slate-800">Maßnahme</div>
                <p className="text-sm text-slate-600">{i.action}</p>
              </div>
              {i.photo_file_id && <div className="mt-2"><FileLink fileId={i.photo_file_id} label="Foto ansehen" /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
